import React, { useState, useCallback, useEffect, useMemo } from "react";
import useStyles from "./style";
import {
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
} from "@mui/material";
import {
  SearchOutlined,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";

import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import moment from "moment-timezone";
import { CUSTOM_CONSTANTS } from "Constants";

// Services
import DriverApi from "Services/Driver";

const Cashoutrecords = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );

  const [row, setRow] = useState([]);
  const [bulkrow, setbulkrow] = useState([]);
  const [searchText, setSearchText] = useState("");
  const timezone = useSelector((state) => state.timezone.timezone);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [statusMap, setStatusMap] = useState({});

  // Get cashout records list api
  const getcashoutrecords = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setTotalPages(res?.data?.data?.totalRecords || 1);
        const cashoutData = res?.data?.data?.data || [];
        setRow(cashoutData);

        const newStatusMap = {};
        cashoutData.forEach((item) => {
          newStatusMap[item._id] = item.status;
        });
        setStatusMap(newStatusMap);
      } else {
        setRow([]);
        setStatusMap({});
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    const params = { page, limit: pageSize };
    if (demoaccount_id) {
      params.demoaccount_id = demoaccount_id;
    }
    DriverApi.getdriverscashoutrecords(params).then(onSuccess, onFailure);
  }, [page, pageSize, demoaccount_id, dispatch]);

  const getAllcashoutrecords = useCallback(() => {
    const onSuccess = (res) => {
      if (res?.data?.status === true) {
        setbulkrow(res?.data?.data?.data || []);
      } else {
        setbulkrow([]);
      }
    };
    const onFailure = () => {
      setbulkrow([]);
    };
    const params = { page: 1, limit: "unlimited" };
    if (demoaccount_id) {
      params.demoaccount_id = demoaccount_id;
    }
    DriverApi.getdriverscashoutrecords(params).then(onSuccess, onFailure);
  }, [demoaccount_id]);

  useEffect(() => {
    getcashoutrecords();
    getAllcashoutrecords();
  }, [getcashoutrecords, getAllcashoutrecords]);

  const calculateSno = (pageIndex, pSize, index) => {
    return (pageIndex - 1) * pSize + index + 1;
  };

  const dataRows = useMemo(() => {
    let filtered = (row || []).filter(
      (data) =>
        data?.driver_details?.user_name &&
        data?.driver_details?.email &&
        data?.status === "PENDING"
    );

    if (searchText.trim()) {
      const query = searchText.toLowerCase();
      filtered = filtered.filter(
        (data) =>
          data?.driver_details?.user_name?.toLowerCase().includes(query) ||
          data?.driver_details?.email?.toLowerCase().includes(query) ||
          data?.driver_details?.mobileNumber?.toString().includes(query)
      );
    }

    return _.map(filtered, (data, index) => ({
      sno: calculateSno(page, pageSize, index),
      username: data?.driver_details?.user_name,
      email: data?.driver_details?.email,
      mobile: `${data?.driver_details?.countryCode || ""} ${
        data?.driver_details?.mobileNumber || ""
      }`,
      status: data?.status,
      amount_earned: (data?.amount_earned ?? 0).toFixed(2),
      platform_fee: (data?.platform_fee ?? 0).toFixed(2),
      cashout_amount: (data?.cashout_amount ?? 0).toFixed(2),
      cashout_usertype: data?.cashout_usertype,
      updatedAt: data?.updatedAt
        ? moment(data?.updatedAt).tz(timezone).format("YYYY-MM-DD hh:mm A")
        : "-",
      row: data,
      driverId: data?.driver_details?._id,
    }));
  }, [row, page, pageSize, searchText, timezone]);

  const formatteddatas = useMemo(() => {
    return dataRows.map((r) => ({
      "S.NO": r.sno,
      "User Name": r.username,
      Email: r.email,
      Mobile: r.mobile,
      Status: r.status,
      "Earned amount": r.amount_earned,
      "Platform fee": r.platform_fee,
      "Cashout amount": `${r.cashout_amount} ${CUSTOM_CONSTANTS.BASE_CURRENCY}`,
      "Updated At": r.updatedAt,
    }));
  }, [dataRows]);

  const bulkdataRows = useMemo(() => {
    return _.map(
      _.orderBy(
        (bulkrow || []).filter(
          (data) =>
            data?.driver_details?.user_name &&
            data?.driver_details?.email &&
            data?.status === "PENDING"
        ),
        ["createdAt"],
        ["asc"]
      ),
      (data, index) => ({
        sno: index + 1,
        username: data?.driver_details?.user_name,
        email: data?.driver_details?.email,
        mobile: `${data?.driver_details?.countryCode || ""} ${
          data?.driver_details?.mobileNumber || ""
        }`,
        status: data?.status,
        amount_earned: (data?.amount_earned ?? 0).toFixed(2),
        platform_fee: (data?.platform_fee ?? 0).toFixed(2),
        cashout_amount: (data?.cashout_amount ?? 0).toFixed(2),
        cashout_usertype: data?.cashout_usertype,
        updatedAt: data?.updatedAt
          ? moment(data?.updatedAt).tz(timezone).format("YYYY-MM-DD hh:mm A")
          : "-",
        row: data,
      })
    );
  }, [bulkrow, timezone]);

  const bulkformatteddatas = useMemo(() => {
    return bulkdataRows.map((r) => ({
      "S.NO": r.sno,
      "User Name": r.username,
      Email: r.email,
      Mobile: r.mobile,
      Status: r.status,
      "Earned amount": r.amount_earned,
      "Platform fee": r.platform_fee,
      "Cashout amount": r.cashout_amount,
      "Updated At": r.updatedAt,
    }));
  }, [bulkdataRows]);

  // Export handlers
  const handleCopy = () => {
    const columnWidths = [5, 15, 25, 15, 15, 15, 15, 15, 15];
    const padText = (text, width) => (text ?? "").toString().padEnd(width, " ");
    const header = [
      padText("S.NO", columnWidths[0]),
      padText("User Name", columnWidths[1]),
      padText("Email", columnWidths[2]),
      padText("Mobile", columnWidths[3]),
      padText("Status", columnWidths[4]),
      padText("Earned amount", columnWidths[5]),
      padText("Platform fee", columnWidths[6]),
      padText("Cashout amount", columnWidths[7]),
      padText("Updated At", columnWidths[8]),
    ].join("");

    const rowsText = dataRows
      .map((r) =>
        [
          padText(r.sno, columnWidths[0]),
          padText(r.username, columnWidths[1]),
          padText(r.email, columnWidths[2]),
          padText(r.mobile, columnWidths[3]),
          padText(r.status, columnWidths[4]),
          padText(r.amount_earned, columnWidths[5]),
          padText(r.platform_fee, columnWidths[6]),
          padText(r.cashout_amount, columnWidths[7]),
          padText(r.updatedAt, columnWidths[8]),
        ].join("")
      )
      .join("\n");

    navigator.clipboard.writeText([header, rowsText].join("\n")).then(() => {
      dispatch(
        showSnackbar({
          message: "Table copied to clipboard!",
          autoHideDuration: 2000,
          variant: "success",
        })
      );
    });
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(formatteddatas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pending Cashouts");
    XLSX.writeFile(workbook, "CashoutrecordsList.xlsx");
  };

  const handleDownloadCSV = () => {
    const csv = Papa.unparse(formatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "CashoutrecordsList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const tableData = dataRows.map((r) => [
      r.sno,
      r.username,
      r.email,
      r.mobile,
      r.status,
      r.amount_earned,
      r.platform_fee,
      r.cashout_amount,
      r.updatedAt,
    ]);
    doc.autoTable({
      head: [
        [
          "S.NO",
          "User Name",
          "Email",
          "Mobile",
          "Status",
          "Earned amount",
          "Platform fee",
          "Cashout amount",
          "Updated At",
        ],
      ],
      body: tableData,
    });
    doc.save("CashoutrecordsList.pdf");
  };

  const handleDownloadAll = () => {
    const csv = Papa.unparse(bulkformatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "All_CashoutrecordsList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleStatusChange = async (id, newStatus, driverId) => {
    dispatch(showLoader("Loading please wait..."));
    setStatusMap((prev) => ({
      ...prev,
      [id]: newStatus,
    }));

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        if (res?.data?.data?.status === "PAID") {
          navigate("/cashoutsapproved");
        }
        if (res?.data?.data?.status === "REJECTED") {
          navigate("/cashoutsrejected");
        }
      } else {
        const originalStatus = row.find((r) => r._id === id)?.status || "PENDING";
        setStatusMap((prev) => ({
          ...prev,
          [id]: originalStatus,
        }));
        getcashoutrecords();
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to update data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    DriverApi.updatedrivercashoutstatus(id, {
      status: newStatus,
      driver_Id: driverId,
    }).then(onSuccess, onFailure);
  };

  // Pagination
  const totalPagesCount = Math.ceil(totalPages / pageSize) || 1;

  const renderPagination = () => {
    let pages = [];
    const internalPage = page - 1;
    if (totalPagesCount <= 5) {
      for (let i = 0; i < totalPagesCount; i++) pages.push(i);
    } else {
      if (internalPage < 3) {
        pages = [0, 1, 2, "...", totalPagesCount - 1];
      } else if (internalPage > totalPagesCount - 4) {
        pages = [0, "...", totalPagesCount - 3, totalPagesCount - 2, totalPagesCount - 1];
      } else {
        pages = [0, "...", internalPage - 1, internalPage, internalPage + 1, "...", totalPagesCount - 1];
      }
    }

    return pages.map((item, index) => {
      if (item === "...") {
        return (
          <span key={index} className={classes.pageDots}>
            ...
          </span>
        );
      }
      return (
        <Button
          key={item}
          className={internalPage === item ? classes.pageButtonActive : classes.pageButton}
          onClick={() => setPage(item + 1)}
        >
          {item + 1}
        </Button>
      );
    });
  };

  const getStatusColor = (st) => {
    switch (st) {
      case "PAID":
        return "#15803d";
      case "REJECTED":
        return "#b91c1c";
      case "PENDING":
      default:
        return "#ed6c02";
    }
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.headerContainer}>
        <Typography className={classes.pageTitle}>Driver Pending Cashouts</Typography>
        <Typography className={classes.breadcrumb}>
          Admin / <span>Driver Pending Cashouts</span>
        </Typography>
      </Box>

      {/* Action Row */}
      <Box className={classes.actionRow}>
        {/* Search */}
        <Box className={classes.searchWrapper}>
          <SearchOutlined className={classes.searchIcon} />
          <input
            type="text"
            placeholder="Search pending cashouts..."
            className={classes.searchInput}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
          />
        </Box>

        {/* Export Group */}
        <Box className={classes.rightActions}>
          <Box className={classes.exportGroup}>
            <Button className={classes.exportButton} onClick={handleCopy}>
              COPY
            </Button>
            <Button className={classes.exportButton} onClick={handleDownloadExcel}>
              EXCEL
            </Button>
            <Button className={classes.exportButton} onClick={handleDownloadCSV}>
              CSV
            </Button>
            <Button className={classes.exportButton} onClick={handleDownloadPDF}>
              PDF
            </Button>
            <Button className={classes.exportButton} onClick={handleDownloadAll}>
              DOWNLOAD ALL
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} className={classes.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeaderRow}>
              <TableCell className={classes.tableHeaderCell}>S.No</TableCell>
              <TableCell className={classes.tableHeaderCell}>Name</TableCell>
              <TableCell className={classes.tableHeaderCell}>Email</TableCell>
              <TableCell className={classes.tableHeaderCell}>Mobile</TableCell>
              <TableCell className={classes.tableHeaderCell}>Status</TableCell>
              <TableCell className={classes.tableHeaderCell}>Cashout Amount</TableCell>
              <TableCell className={classes.tableHeaderCell}>Updated At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataRows.map((rowItem) => {
              const currentStatus = statusMap[rowItem.row._id] || "PENDING";
              return (
                <TableRow key={rowItem.sno} className={classes.tableRow}>
                  <TableCell className={classes.tableCell}>{rowItem.sno}</TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.boldText}`}>
                    {rowItem.username}
                  </TableCell>
                  <TableCell className={classes.tableCell}>{rowItem.email}</TableCell>
                  <TableCell className={classes.tableCell}>{rowItem.mobile}</TableCell>
                  <TableCell className={classes.tableCell}>
                    <Select
                      value={currentStatus}
                      onChange={(e) =>
                        handleStatusChange(rowItem.row._id, e.target.value, rowItem.driverId)
                      }
                      variant="standard"
                      size="small"
                      className={classes.statusSelect}
                      sx={{ minWidth: 110, color: getStatusColor(currentStatus) }}
                    >
                      <MenuItem value="PENDING" sx={{ color: "#ed6c02", fontSize: 13, fontWeight: 600 }}>
                        PENDING
                      </MenuItem>
                      <MenuItem value="PAID" sx={{ color: "#15803d", fontSize: 13, fontWeight: 600 }}>
                        APPROVED
                      </MenuItem>
                      <MenuItem value="REJECTED" sx={{ color: "#b91c1c", fontSize: 13, fontWeight: 600 }}>
                        REJECTED
                      </MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {rowItem.cashout_amount} {CUSTOM_CONSTANTS.BASE_CURRENCY}
                  </TableCell>
                  <TableCell className={classes.tableCell}>{rowItem.updatedAt}</TableCell>
                </TableRow>
              );
            })}
            {dataRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} style={{ textAlign: "center", padding: "40px" }}>
                  <Typography variant="body2" color="textSecondary">
                    No pending cashout records found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box className={classes.paginationContainer}>
          <Typography className={classes.paginationText}>
            Showing {(page - 1) * pageSize + (dataRows.length > 0 ? 1 : 0)} to{" "}
            {Math.min(page * pageSize, totalPages)} of {totalPages} results
          </Typography>

          <Box className={classes.paginationControls}>
            <ChevronLeft
              className={`${classes.pageArrow} ${page === 1 ? "disabled" : ""}`}
              onClick={() => page > 1 && setPage(page - 1)}
            />
            {renderPagination()}
            <ChevronRight
              className={`${classes.pageArrow} ${page === totalPagesCount ? "disabled" : ""}`}
              onClick={() => page < totalPagesCount && setPage(page + 1)}
            />
          </Box>
        </Box>
      </TableContainer>
    </Box>
  );
};

export default Cashoutrecords;
